<template>
  <div class="chatbox">
    <div id="chat-messages" class="chat-messages">
      <div v-if="messagesExist">
        <div v-for="message in messages" :key="message.id">
          <div class="date-messages">{{ formatMessageDate(message.createdAt) }}</div>
          <div class="message" :class="messageClass(message)">
            <div
              class="message-text"
              :class="messageTextClass(message)"
              v-html="formatMessageContent(message)"
            ></div>
            <div class="clearfix">
              <small class="footer-message float-left mr-2" :class="messageTextClass(message)">{{
                getFormattedDate(message.createdAt)
              }}</small>
              <small
                v-if="!fromSession(message)"
                class="footer-message float-right"
                :class="messageTextClass(message)"
              >
                {{ getAuthorName(message) }}
              </small>
              <small v-else class="footer-message other-message-text float-right">{{
                $t('src/components/messagedisplay.8030')
              }}</small>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="not-messages">{{ $t('src/components/messagedisplay.4718') }}</div>
    </div>
  </div>
</template>

<script>
import { nameMonths } from '@/mixins/name-months.js'
import { DATE_FORMAT, messageTypes } from '@/main.config'
import declension from '@/modules/declension'

export default {
  name: 'MessageDisplay',
  mixins: [nameMonths],
  props: {
    messages: {
      type: Array,
      required: true
    }
  },
  computed: {
    messagesExist() {
      return this.messages && this.messages.length > 0
    }
  },
  created() {
    this.old_unique_date = null
  },
  mounted() {
    const listElm = document.querySelector('#chat-messages')
    listElm.addEventListener('scroll', () => {
      if (listElm.scrollTop <= 10) {
        this.loadMore()
      }
    })
    this.scrollToBottom()
  },
  beforeUpdate() {
    this.old_unique_date = null
  },

  methods: {
    scrollToBottom() {
      var element = document.getElementById('chat-messages')
      if (element) {
        element.scrollTop = element.scrollHeight
      }
    },
    loadMore() {
      this.$emit('load:messages')
    },
    getFormattedDate(date) {
      return this.moment
        .utc(date, DATE_FORMAT.serverFull)
        .local()
        .format(DATE_FORMAT.timeHM)
    },
    isMyMessage(message) {
      return this.$store.getters.AUTH_USER.id === message.user_id
    },
    messageClass(message) {
      return this.isMyMessage(message) ? 'my-message' : 'other-message'
    },
    messageTextClass(message) {
      return this.isMyMessage(message) ? 'my-message-text' : 'other-message-text'
    },
    formatMessageDate(date) {
      this.moment.locale('ru')
      let old_date = this.old_unique_date
      let new_date = this.moment(date).format('DD.MM')
      if (old_date != new_date) {
        this.old_unique_date = new_date
        return this.getGenMonthWithData(new_date)
      }
    },
    formatMessageContent(message) {
      switch (message.type) {
        case messageTypes.saturation:
          return `${this.$t('events.eventSpO2')}: ${message.content}%`
        case messageTypes.pressure:
          return `${this.$t('src/components/chat.pressure')}: ${message.content} ${this.$t('unit.mmHg')}`
        case messageTypes.glucose:
          return `${this.$t('src/components/chat.glucose')}: ${message.content} ${this.$t('unit.mmolL')}`
        case messageTypes.diary:
          return this.formatDiaryMessage(message.content)
        default:
          return message.content
      }
    },
    formatDiaryMessage(content) {
      let contentObj = JSON.parse(content)
      let resultText = ''

      // Одышка в покое
      resultText += `${this.$t('courseReport.dyspnea')} ${this.$tc(
        'courseReport.atRest',
        2
      )}: ${contentObj.breath_shortness_at_rest_intensity || 0} - ${this.getIntensiveName(
        contentObj.breath_shortness_at_rest_intensity
      )}<br /><br />`

      // Одышка при нагрузке
      resultText += `${this.$t('courseReport.dyspnea')} ${this.$tc(
        'courseReport.withexertion',
        2
      )}: ${contentObj.breath_shortness_on_exertion_intensity || 0} - ${this.getIntensiveName(
        contentObj.breath_shortness_on_exertion_intensity
      )}<br /><br />`

      // Боль в сердце
      resultText += `${this.$t('courseReport.heartpain')}: ${contentObj.heart_pain_attacks_number ||
        0} ${declension(contentObj.heart_pain_attacks_number, [
        this.$tc('courseReport.attack', 0),
        this.$tc('courseReport.attack', 1),
        this.$tc('courseReport.attack', 2)
      ])}<br /><br />`

      // Боль за грудиной
      resultText += `${this.$t('courseReport.chestpain')} ${this.$tc(
        'courseReport.withexertion',
        2
      )}: ${contentObj.pain_behind_chest_intensity || 0} - ${this.getIntensiveName(
        contentObj.pain_behind_chest_intensity
      )}<br /><br />`

      // Вес
      resultText += `${this.$t('courseReport.weight')}: ${contentObj.body_weigth || 0} ${this.$t('unit.kg')}`

      // Комментарий
      resultText += contentObj.comment
        ? `<br /><br />${this.$t('courseReport.comment')}: ${contentObj.comment}`
        : ''

      return resultText
    },
    getIntensiveName(score) {
      if (score >= 1 && score <= 3) {
        return this.$tc('courseReport.mild', 1)
      } else if (score >= 4 && score <= 6) {
        return this.$tc('courseReport.moderate', 1)
      } else if (score === 7 || score === 8) {
        return this.$tc('courseReport.severe', 1)
      } else if (score === 9 || score === 10) {
        return this.$tc('courseReport.extreme', 1)
      } else {
        return this.$tc('courseReport.notbothered', 1)
      }
    },
    getAuthorName(message) {
      if (message.user_id) {
        if (this.isNeuroAdmin) return String(message.user_id).padStart(5, '0')
        else return `${message.user.name} ${message.user.surname}`
      } else {
        if (this.isNeuroAdmin) return String(message.user_id).padStart(5, '0')
        else return `${message.patient.name} ${message.patient.surname}`
      }
    },
    fromSession(message) {
      return Boolean(message.isFromSession)
    }
  }
}
</script>
